/**
 * MemoryManager - Intelligent memory management for large legal documents
 * 
 * Provides advanced memory optimization, garbage collection hints,
 * and memory leak prevention for sustained document editing sessions.
 */

import { BrowserAPI } from '../types/BrowserTypes';

interface MemoryPool {
  poolId: string;
  type: 'text' | 'glyph' | 'layout' | 'cache' | 'temp';
  allocated: number;    // Bytes allocated
  used: number;        // Bytes currently used
  peak: number;        // Peak usage
  objects: number;     // Number of objects
  lastCleanup: Date;
}

interface MemoryMetrics {
  totalAllocated: number;  // Total memory allocated (MB)
  totalUsed: number;      // Total memory used (MB)
  totalAvailable: number; // Available memory (MB)
  gcFrequency: number;    // GC events per minute
  memoryPressure: number; // Memory pressure (0-1)
  leakDetected: boolean;  // Memory leak detection
  pools: { [poolId: string]: MemoryPool };
}

interface MemoryOptimization {
  type: 'gc_hint' | 'pool_cleanup' | 'cache_clear' | 'object_pool' | 'weak_ref';
  description: string;
  memoryFreed: number;
  executionTime: number;
  success: boolean;
}

interface ObjectPool<T> {
  create: () => T;
  reset: (obj: T) => void;
  destroy: (obj: T) => void;
  available: T[];
  inUse: Set<T>;
  maxSize: number;
}

export class MemoryManager {
  private memoryThreshold: number;
  private memoryPools: Map<string, MemoryPool> = new Map();
  private objectPools: Map<string, ObjectPool<any>> = new Map();
  private metrics: MemoryMetrics;
  private constrainedMode: boolean = false;
  private lastEmergencyAt: number = 0;
  
  // Memory monitoring
  private memoryObserver: PerformanceObserver | null = null;
  private gcCounter: number = 0;
  private lastGcTime: Date = new Date();
  private memoryHistory: number[] = [];
  
  // Cleanup timers
  private cleanupTimer: NodeJS.Timeout | null = null;
  private monitoringTimer: NodeJS.Timeout | null = null;
  
  // Configuration
  private enableAggressiveCleanup: boolean = false;
  private cleanupInterval: number = 30000; // 30 seconds
  private memoryCheckInterval: number = 5000; // 5 seconds

  constructor(memoryThreshold: number = 100) {
    this.memoryThreshold = memoryThreshold;
    
    this.metrics = {
      totalAllocated: 0,
      totalUsed: 0,
      totalAvailable: 0,
      gcFrequency: 0,
      memoryPressure: 0,
      leakDetected: false,
      pools: {}
    };
    
    // Detect environment memory capabilities and adjust thresholds/intervals
    try {
      const memInfo = BrowserAPI.getPerformanceMemory();
      const jsHeapLimitMb = memInfo ? (memInfo.jsHeapSizeLimit / (1024 * 1024)) : null;
      this.constrainedMode = !memInfo || (jsHeapLimitMb !== null && jsHeapLimitMb < 256);
      if (jsHeapLimitMb) {
        // Use at least 50% of actual heap limit or provided threshold, whichever is higher
        const adaptiveThreshold = Math.max(memoryThreshold, Math.floor(jsHeapLimitMb * 0.5));
        this.memoryThreshold = adaptiveThreshold;
      } else {
        // No reliable memory info (WebKit/WKWebView). Use a safer threshold.
        this.memoryThreshold = Math.max(memoryThreshold, 512);
      }
      if (this.constrainedMode) {
        // Slow down monitors in constrained environments
        this.memoryCheckInterval = 10000; // 10s
        this.cleanupInterval = 60000; // 60s
      }
    } catch {}

    this.initializeMemoryPools();
    this.startMemoryMonitoring();
    this.setupPerformanceObserver();
  }

  // ============================================================================
  // MEMORY POOL MANAGEMENT
  // ============================================================================

  /**
   * Initialize memory pools for different object types
   */
  private initializeMemoryPools(): void {
    // Text content pool
    this.createMemoryPool('text', 'text');
    
    // Glyph data pool
    this.createMemoryPool('glyphs', 'glyph');
    
    // Layout calculation pool
    this.createMemoryPool('layout', 'layout');
    
    // Rendering cache pool
    this.createMemoryPool('cache', 'cache');
    
    // Temporary objects pool
    this.createMemoryPool('temp', 'temp');
    
    console.log(`[MemoryManager] Initialized ${this.memoryPools.size} memory pools`);
  }

  /**
   * Create memory pool
   */
  private createMemoryPool(poolId: string, type: MemoryPool['type']): void {
    const pool: MemoryPool = {
      poolId,
      type,
      allocated: 0,
      used: 0,
      peak: 0,
      objects: 0,
      lastCleanup: new Date()
    };
    
    this.memoryPools.set(poolId, pool);
  }

  /**
   * Allocate memory from pool
   */
  allocateFromPool(poolId: string, size: number): boolean {
    const pool = this.memoryPools.get(poolId);
    if (!pool) return false;
    
    // Check if allocation would exceed threshold
    const newTotal = this.metrics.totalAllocated + (size / (1024 * 1024));
    if (newTotal > this.memoryThreshold) {
      console.warn(`[MemoryManager] Allocation would exceed threshold: ${newTotal.toFixed(1)}MB`);
      return false;
    }
    
    // Allocate memory
    pool.allocated += size;
    pool.used += size;
    pool.objects++;
    pool.peak = Math.max(pool.peak, pool.used);
    
    this.updateTotalMetrics();
    
    return true;
  }

  /**
   * Free memory from pool
   */
  freeFromPool(poolId: string, size: number): void {
    const pool = this.memoryPools.get(poolId);
    if (!pool) return;
    
    pool.used = Math.max(0, pool.used - size);
    pool.objects = Math.max(0, pool.objects - 1);
    
    this.updateTotalMetrics();
  }

  /**
   * Cleanup memory pool
   */
  async cleanupPool(poolId: string): Promise<MemoryOptimization> {
    const pool = this.memoryPools.get(poolId);
    if (!pool) {
      return {
        type: 'pool_cleanup',
        description: `Pool ${poolId} not found`,
        memoryFreed: 0,
        executionTime: 0,
        success: false
      };
    }
    
    const startTime = performance.now();
    const initialUsed = pool.used;
    
    try {
      // Pool-specific cleanup
      switch (pool.type) {
        case 'cache':
          await this.cleanupCachePool(pool);
          break;
          
        case 'temp':
          await this.cleanupTempPool(pool);
          break;
          
        case 'glyph':
          await this.cleanupGlyphPool(pool);
          break;
          
        default:
          await this.cleanupGenericPool(pool);
      }
      
      pool.lastCleanup = new Date();
      
      const memoryFreed = initialUsed - pool.used;
      const executionTime = performance.now() - startTime;
      
      if (memoryFreed > 0) {
        console.log(`[MemoryManager] Cleaned up pool ${poolId}: ${(memoryFreed / 1024).toFixed(1)}KB freed`);
      }
      
      return {
        type: 'pool_cleanup',
        description: `Cleaned up ${poolId} pool`,
        memoryFreed: memoryFreed / (1024 * 1024), // Convert to MB
        executionTime,
        success: true
      };
      
    } catch (error) {
      console.error(`[MemoryManager] Pool cleanup failed for ${poolId}:`, error);
      
      return {
        type: 'pool_cleanup',
        description: `Pool cleanup failed: ${error}`,
        memoryFreed: 0,
        executionTime: performance.now() - startTime,
        success: false
      };
    }
  }

  // ============================================================================
  // OBJECT POOLING
  // ============================================================================

  /**
   * Create object pool for reusable objects
   */
  createObjectPool<T>(
    poolId: string,
    createFn: () => T,
    resetFn: (obj: T) => void,
    destroyFn: (obj: T) => void,
    maxSize: number = 100
  ): void {
    const pool: ObjectPool<T> = {
      create: createFn,
      reset: resetFn,
      destroy: destroyFn,
      available: [],
      inUse: new Set(),
      maxSize
    };
    
    this.objectPools.set(poolId, pool);
    
    console.log(`[MemoryManager] Created object pool: ${poolId} (max: ${maxSize})`);
  }

  /**
   * Get object from pool
   */
  getFromPool<T>(poolId: string): T | null {
    const pool = this.objectPools.get(poolId) as ObjectPool<T> | undefined;
    if (!pool) return null;
    
    let obj: T;
    
    if (pool.available.length > 0) {
      // Reuse existing object
      obj = pool.available.pop()!;
      pool.reset(obj);
    } else {
      // Create new object
      obj = pool.create();
    }
    
    pool.inUse.add(obj);
    return obj;
  }

  /**
   * Return object to pool
   */
  returnToPool<T>(poolId: string, obj: T): void {
    const pool = this.objectPools.get(poolId) as ObjectPool<T> | undefined;
    if (!pool || !pool.inUse.has(obj)) return;
    
    pool.inUse.delete(obj);
    
    if (pool.available.length < pool.maxSize) {
      // Return to available pool
      pool.reset(obj);
      pool.available.push(obj);
    } else {
      // Pool is full, destroy object
      pool.destroy(obj);
    }
  }

  // ============================================================================
  // GARBAGE COLLECTION
  // ============================================================================

  /**
   * Suggest garbage collection
   */
  suggestGarbageCollection(): MemoryOptimization {
    const startTime = performance.now();
    const initialMemory = this.getCurrentUsage();
    
    try {
      // Hint for garbage collection
      BrowserAPI.callGarbageCollection();
      
      // Clear weak references
      this.clearWeakReferences();
      
      // Force finalization
      if ('FinalizationRegistry' in window) {
        // Modern browsers support FinalizationRegistry
        this.triggerFinalization();
      }
      
      const finalMemory = this.getCurrentUsage();
      const memoryFreed = initialMemory - finalMemory;
      const executionTime = performance.now() - startTime;
      
      this.gcCounter++;
      this.lastGcTime = new Date();
      
      if (memoryFreed >= 8) {
        console.log(`[MemoryManager] GC suggested: ${memoryFreed.toFixed(1)}MB freed`);
      }
      
      return {
        type: 'gc_hint',
        description: 'Garbage collection suggested',
        memoryFreed,
        executionTime,
        success: memoryFreed > 0
      };
      
    } catch (error) {
      console.error('[MemoryManager] GC suggestion failed:', error);
      
      return {
        type: 'gc_hint',
        description: `GC suggestion failed: ${error}`,
        memoryFreed: 0,
        executionTime: performance.now() - startTime,
        success: false
      };
    }
  }

  private clearWeakReferences(): void {
    // Clear any weak references that might be holding objects
    // Implementation depends on actual weak reference usage
  }

  private triggerFinalization(): void {
    // Trigger finalization for objects with finalizers
    // Implementation depends on actual FinalizationRegistry usage
  }

  // ============================================================================
  // MEMORY MONITORING
  // ============================================================================

  /**
   * Start continuous memory monitoring
   */
  private startMemoryMonitoring(): void {
    this.monitoringTimer = setInterval(() => {
      this.updateMemoryMetrics();
      this.checkMemoryPressure();
      this.detectMemoryLeaks();
    }, this.memoryCheckInterval);
    
    // Start cleanup timer
    this.cleanupTimer = setInterval(() => {
      this.performScheduledCleanup();
    }, this.cleanupInterval);
    
    console.log('[MemoryManager] Memory monitoring started');
  }

  /**
   * Update memory metrics
   */
  private updateMemoryMetrics(): void {
    const currentUsage = this.getCurrentUsage();
    
    // Update metrics
    this.metrics.totalUsed = currentUsage;
    this.metrics.totalAllocated = this.calculateTotalAllocated();
    this.metrics.totalAvailable = this.estimateAvailableMemory();
    
    // Calculate memory pressure
    this.metrics.memoryPressure = this.metrics.totalUsed / this.memoryThreshold;
    
    // Update GC frequency
    const now = Date.now();
    const timeSinceLastGc = now - this.lastGcTime.getTime();
    this.metrics.gcFrequency = timeSinceLastGc > 0 ? (60000 / timeSinceLastGc) : 0;
    
    // Add to history
    this.memoryHistory.push(currentUsage);
    if (this.memoryHistory.length > 100) {
      this.memoryHistory = this.memoryHistory.slice(-50);
    }
    
    // Update pool metrics
    for (const [poolId, pool] of this.memoryPools) {
      this.metrics.pools[poolId] = { ...pool };
    }
  }

  /**
   * Check for memory pressure
   */
  private checkMemoryPressure(): void {
    const pressureThreshold = this.constrainedMode ? 0.95 : 0.8;
    if (this.metrics.memoryPressure > pressureThreshold) {
      const now = Date.now();
      // Rate-limit emergency cleanups to at most once every 30s
      if (now - this.lastEmergencyAt > 30000) {
        console.warn(`[MemoryManager] High memory pressure: ${(this.metrics.memoryPressure * 100).toFixed(1)}%`);
        this.enableAggressiveCleanup = true;
        this.lastEmergencyAt = now;
        this.performEmergencyCleanup();
      }
    } else if (this.metrics.memoryPressure < 0.5) {
      this.enableAggressiveCleanup = false;
    }
  }

  /**
   * Detect memory leaks
   */
  private detectMemoryLeaks(): void {
    if (this.memoryHistory.length < 10) return;
    
    // Check for sustained memory growth
    const recent = this.memoryHistory.slice(-10);
    const older = this.memoryHistory.slice(-20, -10);
    
    if (recent.length === 10 && older.length === 10) {
      const recentAvg = recent.reduce((sum, val) => sum + val, 0) / recent.length;
      const olderAvg = older.reduce((sum, val) => sum + val, 0) / older.length;
      
      const growthRate = (recentAvg - olderAvg) / olderAvg;
      
      if (growthRate > 0.1) { // 10% growth
        this.metrics.leakDetected = true;
        console.error(`[MemoryManager] Potential memory leak detected: ${(growthRate * 100).toFixed(1)}% growth`);
        
        // Emit leak detection event
        const event = new CustomEvent('edgedocs-memory-leak', {
          detail: { growthRate, currentUsage: recentAvg }
        });
        window.dispatchEvent(event);
      } else {
        this.metrics.leakDetected = false;
      }
    }
  }

  // ============================================================================
  // MEMORY OPTIMIZATION
  // ============================================================================

  /**
   * Perform scheduled cleanup
   */
  private async performScheduledCleanup(): Promise<void> {
    const optimizations: MemoryOptimization[] = [];
    
    // Cleanup each pool
    for (const poolId of this.memoryPools.keys()) {
      const optimization = await this.cleanupPool(poolId);
      optimizations.push(optimization);
    }
    
    // Cleanup object pools
    for (const poolId of this.objectPools.keys()) {
      const optimization = await this.cleanupObjectPool(poolId);
      optimizations.push(optimization);
    }
    
    // Suggest GC if memory pressure is high
    if (this.metrics.memoryPressure > 0.7) {
      const gcOptimization = this.suggestGarbageCollection();
      optimizations.push(gcOptimization);
    }
    
    const totalFreed = optimizations.reduce((sum, opt) => sum + opt.memoryFreed, 0);
    if (totalFreed > 0) {
      console.log(`[MemoryManager] Scheduled cleanup complete: ${totalFreed.toFixed(1)}MB freed`);
    }
  }

  /**
   * Perform emergency cleanup when memory pressure is critical
   */
  private async performEmergencyCleanup(): Promise<void> {
    const before = this.getCurrentUsage();
    console.warn('[MemoryManager] Performing emergency cleanup...');
    
    // Clear all temporary pools immediately
    await this.clearPool('temp');
    await this.clearPool('cache');
    
    // Reduce object pool sizes
    for (const [poolId, pool] of this.objectPools) {
      this.reduceObjectPoolSize(poolId, 0.5); // Reduce by 50%
    }
    
    // Force garbage collection
    this.suggestGarbageCollection();
    
    const after = this.getCurrentUsage();
    const freed = Math.max(0, before - after);
    if (freed > 0) {
      console.log(`[MemoryManager] Emergency cleanup complete: ${freed.toFixed(1)}MB freed`);
    }
  }

  // ============================================================================
  // POOL CLEANUP METHODS
  // ============================================================================

  private async cleanupCachePool(pool: MemoryPool): Promise<void> {
    // Clear least recently used cache entries
    const freedMemory = pool.used * 0.3; // Free 30% of cache
    pool.used -= freedMemory;
    pool.objects = Math.floor(pool.objects * 0.7);
  }

  private async cleanupTempPool(pool: MemoryPool): Promise<void> {
    // Clear all temporary objects
    pool.used = 0;
    pool.objects = 0;
  }

  private async cleanupGlyphPool(pool: MemoryPool): Promise<void> {
    // Clear old glyph cache entries
    const freedMemory = pool.used * 0.2; // Free 20% of glyphs
    pool.used -= freedMemory;
    pool.objects = Math.floor(pool.objects * 0.8);
  }

  private async cleanupGenericPool(pool: MemoryPool): Promise<void> {
    // Generic cleanup - free 10% of pool
    const freedMemory = pool.used * 0.1;
    pool.used -= freedMemory;
    pool.objects = Math.floor(pool.objects * 0.9);
  }

  private async clearPool(poolId: string): Promise<void> {
    const pool = this.memoryPools.get(poolId);
    if (pool) {
      const hadUsage = pool.used > 0 || pool.objects > 0;
      pool.used = 0;
      pool.objects = 0;
      if (hadUsage) {
        console.log(`[MemoryManager] Cleared pool: ${poolId}`);
      }
    }
  }

  private async cleanupObjectPool(poolId: string): Promise<MemoryOptimization> {
    const pool = this.objectPools.get(poolId);
    if (!pool) {
      return {
        type: 'object_pool',
        description: `Object pool ${poolId} not found`,
        memoryFreed: 0,
        executionTime: 0,
        success: false
      };
    }
    
    const startTime = performance.now();
    const initialSize = pool.available.length;
    
    // Destroy excess available objects
    const targetSize = Math.floor(pool.maxSize * 0.7);
    
    while (pool.available.length > targetSize) {
      const obj = pool.available.pop();
      if (obj) {
        pool.destroy(obj);
      }
    }
    
    const objectsDestroyed = initialSize - pool.available.length;
    const estimatedMemoryFreed = objectsDestroyed * 1024; // Estimate 1KB per object
    
    return {
      type: 'object_pool',
      description: `Cleaned up object pool ${poolId}`,
      memoryFreed: estimatedMemoryFreed / (1024 * 1024),
      executionTime: performance.now() - startTime,
      success: true
    };
  }

  private reduceObjectPoolSize(poolId: string, factor: number): void {
    const pool = this.objectPools.get(poolId);
    if (!pool) return;
    
    const newMaxSize = Math.floor(pool.maxSize * factor);
    
    // Destroy excess objects
    while (pool.available.length > newMaxSize) {
      const obj = pool.available.pop();
      if (obj) {
        pool.destroy(obj);
      }
    }
    
    pool.maxSize = newMaxSize;
    
    console.log(`[MemoryManager] Reduced object pool ${poolId} to ${newMaxSize}`);
  }

  // ============================================================================
  // MEMORY MEASUREMENT
  // ============================================================================

  /**
   * Get current memory usage
   */
  getCurrentUsage(): number {
    const memInfo = BrowserAPI.getPerformanceMemory();
    if (memInfo) {
      return memInfo.usedJSHeapSize / (1024 * 1024); // Convert to MB
    }
    
    // Fallback estimation
    return this.estimateMemoryUsage();
  }

  private estimateMemoryUsage(): number {
    // Estimate based on pool usage
    let totalUsage = 0;
    
    for (const pool of this.memoryPools.values()) {
      totalUsage += pool.used;
    }
    
    return totalUsage / (1024 * 1024); // Convert to MB
  }

  private calculateTotalAllocated(): number {
    let totalAllocated = 0;
    
    for (const pool of this.memoryPools.values()) {
      totalAllocated += pool.allocated;
    }
    
    return totalAllocated / (1024 * 1024); // Convert to MB
  }

  private estimateAvailableMemory(): number {
    const memInfo = BrowserAPI.getPerformanceMemory();
    if (memInfo) {
      return (memInfo.jsHeapSizeLimit - memInfo.usedJSHeapSize) / (1024 * 1024);
    }
    
    // Fallback estimation
    return Math.max(0, this.memoryThreshold - this.metrics.totalUsed);
  }

  private updateTotalMetrics(): void {
    this.metrics.totalUsed = this.getCurrentUsage();
    this.metrics.totalAllocated = this.calculateTotalAllocated();
    this.metrics.totalAvailable = this.estimateAvailableMemory();
  }

  // ============================================================================
  // PERFORMANCE OBSERVER
  // ============================================================================

  private setupPerformanceObserver(): void {
    if ('PerformanceObserver' in window) {
      try {
        this.memoryObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'measure' && entry.name.includes('gc')) {
              this.gcCounter++;
            }
          }
        });
        
        this.memoryObserver.observe({ entryTypes: ['measure'] });
        
        console.log('[MemoryManager] Performance observer active');
        
      } catch (error) {
        console.warn('[MemoryManager] Performance observer not available:', error);
      }
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get memory metrics
   */
  getMemoryMetrics(): MemoryMetrics {
    this.updateMemoryMetrics();
    return { ...this.metrics };
  }

  /**
   * Get memory pool information
   */
  getPoolInfo(): { [poolId: string]: MemoryPool } {
    const poolInfo: { [poolId: string]: MemoryPool } = {};
    
    for (const [poolId, pool] of this.memoryPools) {
      poolInfo[poolId] = { ...pool };
    }
    
    return poolInfo;
  }

  /**
   * Force memory optimization
   */
  async forceOptimization(): Promise<MemoryOptimization[]> {
    console.log('[MemoryManager] Force optimization requested');
    
    const optimizations: MemoryOptimization[] = [];
    
    // Cleanup all pools
    for (const poolId of this.memoryPools.keys()) {
      const optimization = await this.cleanupPool(poolId);
      optimizations.push(optimization);
    }
    
    // Suggest GC
    const gcOptimization = this.suggestGarbageCollection();
    optimizations.push(gcOptimization);
    
    return optimizations;
  }

  /**
   * Update memory threshold
   */
  updateThreshold(newThreshold: number): void {
    this.memoryThreshold = newThreshold;
    console.log(`[MemoryManager] Memory threshold updated to ${newThreshold}MB`);
  }

  /**
   * Check if memory usage is within limits
   */
  isMemoryHealthy(): boolean {
    return this.metrics.memoryPressure < 0.8 && !this.metrics.leakDetected;
  }

  /**
   * Get memory optimization suggestions
   */
  getOptimizationSuggestions(): string[] {
    const suggestions: string[] = [];
    
    if (this.metrics.memoryPressure > 0.8) {
      suggestions.push('Reduce number of visible document chunks');
      suggestions.push('Clear rendering cache');
      suggestions.push('Enable aggressive cleanup mode');
    }
    
    if (this.metrics.leakDetected) {
      suggestions.push('Investigate potential memory leaks');
      suggestions.push('Restart document session');
      suggestions.push('Contact technical support');
    }
    
    if (this.metrics.gcFrequency > 10) {
      suggestions.push('Reduce object allocation rate');
      suggestions.push('Increase object pool sizes');
      suggestions.push('Optimize data structures');
    }
    
    return suggestions;
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Destroy memory manager
   */
  destroy(): void {
    // Stop monitoring
    if (this.monitoringTimer) {
      clearInterval(this.monitoringTimer);
    }
    
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    
    // Stop performance observer
    if (this.memoryObserver) {
      this.memoryObserver.disconnect();
    }
    
    // Clear all pools
    this.memoryPools.clear();
    
    // Destroy all object pools
    for (const [poolId, pool] of this.objectPools) {
      // Destroy all objects in pool
      for (const obj of pool.available) {
        pool.destroy(obj);
      }
      for (const obj of pool.inUse) {
        pool.destroy(obj);
      }
    }
    this.objectPools.clear();
    
    // Final GC suggestion
    this.suggestGarbageCollection();
    
    console.log('[MemoryManager] Destroyed');
  }
}



